import { geoCentroid, geoNaturalEarth1, geoPath, type GeoPermissibleObjects } from "d3-geo";
import { useEffect, useMemo, useRef, useState } from "react";
import { feature } from "topojson-client";
import type { Topology, GeometryCollection } from "topojson-specification";
import countriesData from "../../worker/games/worldle/countries.json";

/**
 * World map that highlights countries by ISO2 code, auto-zoomed to the highlighted
 * set (+ padding), and pannable/zoomable on top of that (drag, pinch, wheel,
 * +/− buttons). Natural Earth 110m via world-atlas (public domain), loaded lazily.
 */
export type MapTone = "start" | "end" | "path" | "current" | "miss";

interface Props {
  tones: Record<string, MapTone>;
  /** Exact fill per ISO2 — wins over `tones` (heat maps etc.). */
  fills?: Record<string, string>;
  height?: number;
}

export type World = GeoJSON.FeatureCollection<GeoJSON.Geometry, { name?: string }>;

export const CCN3_TO_ISO2 = new Map((countriesData as { code: string; ccn3: string }[]).map((c) => [c.ccn3, c.code]));
// Natural Earth quirks: Kosovo has id "-99", Northern Cyprus etc. — map what we can.
CCN3_TO_ISO2.set("-99", "XK");

const FILL: Record<MapTone, string> = {
  start: "#8b8b8b",
  end: "#2ea6ff",
  path: "#5ba85b",
  current: "#f0c419",
  miss: "#c94f4f",
};

let worldPromise: Promise<World> | null = null;
export function loadWorld(): Promise<World> {
  worldPromise ??= import("world-atlas/countries-110m.json").then((m) => {
    const topo = (m.default ?? m) as unknown as Topology<{ countries: GeometryCollection<{ name?: string }> }>;
    return feature(topo, topo.objects.countries) as unknown as World;
  });
  return worldPromise;
}

const WIDTH = 360;
const MIN_K = 1;
const MAX_K = 14;

interface Transform {
  k: number;
  x: number;
  y: number;
}

const IDENTITY: Transform = { k: 1, x: 0, y: 0 };

export function WorldMap({ tones, fills, height = 180 }: Props) {
  const [world, setWorld] = useState<World | null>(null);
  const [t, setT] = useState<Transform>(IDENTITY);
  const svgRef = useRef<SVGSVGElement>(null);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinchDist = useRef<number | null>(null);

  useEffect(() => {
    void loadWorld().then(setWorld);
  }, []);

  const width = WIDTH;
  const { paths, viewBox } = useMemo(() => {
    if (!world) return { paths: [] as { d: string; fill: string; stroke: string }[], viewBox: `0 0 ${width} ${height}` };
    const projection = geoNaturalEarth1();
    const highlighted = world.features.filter((f) => {
      const iso = CCN3_TO_ISO2.get(String(f.id)) ?? "";
      return tones[iso] || fills?.[iso];
    });
    // Fit to the highlighted countries (or the whole world when none yet).
    const target: GeoPermissibleObjects =
      highlighted.length > 0 ? { type: "FeatureCollection", features: highlighted } : world;
    projection.fitExtent(
      [
        [12, 12],
        [width - 12, height - 12],
      ],
      target,
    );
    // Don't zoom in absurdly on tiny countries: cap the scale relative to the world fit
    // and keep the route centred.
    const worldScale = geoNaturalEarth1().fitExtent([[12, 12], [width - 12, height - 12]], world).scale();
    const cap = worldScale * 7;
    if (projection.scale() > cap) {
      projection.scale(cap).center(geoCentroid(target)).translate([width / 2, height / 2]);
    }
    const path = geoPath(projection);
    const out = world.features.map((f) => {
      const iso = CCN3_TO_ISO2.get(String(f.id)) ?? "";
      const tone = tones[iso];
      const custom = fills?.[iso];
      return {
        d: path(f) ?? "",
        fill: custom ?? (tone ? FILL[tone] : "var(--map-land)"),
        stroke: custom || tone ? "rgba(0,0,0,0.35)" : "var(--map-border)",
      };
    });
    return { paths: out, viewBox: `0 0 ${width} ${height}` };
  }, [world, tones, fills, height]);

  /** Client px → viewBox units. */
  const toLocal = (e: { clientX: number; clientY: number }) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return { x: ((e.clientX - rect.left) / rect.width) * width, y: ((e.clientY - rect.top) / rect.height) * height };
  };

  const clamp = (next: Transform): Transform => {
    const k = Math.min(MAX_K, Math.max(MIN_K, next.k));
    if (k === 1) return IDENTITY;
    // Keep at least a sliver of map in view.
    const x = Math.min(width * 0.9, Math.max(-width * (k - 0.1), next.x));
    const y = Math.min(height * 0.9, Math.max(-height * (k - 0.1), next.y));
    return { k, x, y };
  };

  /** Zoom by factor `f` keeping the local point (cx, cy) fixed. */
  const zoomAt = (cx: number, cy: number, f: number) =>
    setT((cur) => {
      const k = Math.min(MAX_K, Math.max(MIN_K, cur.k * f));
      const real = k / cur.k;
      return clamp({ k, x: cx - (cx - cur.x) * real, y: cy - (cy - cur.y) * real });
    });

  const twoPointers = (): [{ x: number; y: number }, { x: number; y: number }] | null => {
    const pts = [...pointers.current.values()];
    const a = pts[0];
    const b = pts[1];
    return a && b ? [a, b] : null;
  };

  const onPointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
    (e.currentTarget as Element).setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pair = twoPointers();
    if (pair) pinchDist.current = Math.hypot(pair[0].x - pair[1].x, pair[0].y - pair[1].y);
  };

  const onPointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const prev = pointers.current.get(e.pointerId);
    if (!prev) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const rect = svgRef.current?.getBoundingClientRect();
    const scale = rect ? width / rect.width : 1;

    const pair = twoPointers();
    if (pair) {
      const [a, b] = pair;
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      if (pinchDist.current && dist > 0) {
        const mid = toLocal({ clientX: (a.x + b.x) / 2, clientY: (a.y + b.y) / 2 });
        zoomAt(mid.x, mid.y, dist / pinchDist.current);
      }
      pinchDist.current = dist;
      return;
    }
    // Single pointer: pan (only useful when zoomed).
    setT((cur) => (cur.k === 1 ? cur : clamp({ ...cur, x: cur.x + (e.clientX - prev.x) * scale, y: cur.y + (e.clientY - prev.y) * scale })));
  };

  const onPointerEnd = (e: React.PointerEvent<SVGSVGElement>) => {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinchDist.current = null;
  };

  const onWheel = (e: React.WheelEvent<SVGSVGElement>) => {
    const p = toLocal(e);
    zoomAt(p.x, p.y, e.deltaY < 0 ? 1.25 : 0.8);
  };

  const zoomed = t.k > 1;

  return (
    <div className="relative">
      <svg
        ref={svgRef}
        viewBox={viewBox}
        width="100%"
        height={height}
        className="block rounded-2xl bg-tgsection"
        style={{ touchAction: "none" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerEnd}
        onPointerCancel={onPointerEnd}
        onWheel={onWheel}
        aria-hidden="true"
      >
        <g transform={`translate(${t.x} ${t.y}) scale(${t.k})`}>
          {paths.map((p, i) => (
            <path key={i} d={p.d} fill={p.fill} stroke={p.stroke} strokeWidth={0.6 / t.k} />
          ))}
        </g>
      </svg>
      <div className="absolute right-1.5 top-1.5 flex flex-col gap-1">
        <MapButton label="+" onClick={() => zoomAt(width / 2, height / 2, 1.5)} />
        <MapButton label="−" onClick={() => zoomAt(width / 2, height / 2, 0.67)} />
        {zoomed && <MapButton label="⌖" onClick={() => setT(IDENTITY)} />}
      </div>
    </div>
  );
}

function MapButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-7 w-7 items-center justify-center rounded-lg bg-[rgba(20,20,20,0.55)] text-sm font-bold text-white active:opacity-70"
    >
      {label}
    </button>
  );
}
